import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// --- 🎨 Global Color Palette ---
const COLOR_PALETTE = [
    { color: 'bg-blue-600', textColor: 'text-blue-600', bgColor: 'bg-blue-100', border: 'border-blue-100' },
    { color: 'bg-purple-600', textColor: 'text-purple-600', bgColor: 'bg-purple-100', border: 'border-purple-100' },
    { color: 'bg-indigo-600', textColor: 'text-indigo-600', bgColor: 'bg-indigo-100', border: 'border-indigo-100' },
    { color: 'bg-pink-600', textColor: 'text-pink-600', bgColor: 'bg-pink-100', border: 'border-pink-100' },
    { color: 'bg-amber-600', textColor: 'text-amber-600', bgColor: 'bg-amber-100', border: 'border-amber-100' },
    { color: 'bg-teal-600', textColor: 'text-teal-600', bgColor: 'bg-teal-100', border: 'border-teal-100' },
    { color: 'bg-rose-600', textColor: 'text-rose-600', bgColor: 'bg-rose-100', border: 'border-rose-100' },
];

// --- 1. Step 1: Approval Line Selection Component ---
const ApprovalLineSection = ({ initialDeptId, docuNm, tableCd, sabun, userName, showMultiLine, approvers, setApprovers, lines, setLines, onSubmit, isProcessing, mode }) => {
    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedDept, setSelectedDept] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dragOverLineId, setDragOverLineId] = useState(null);
    useEffect(() => {
        const fetchOrg = async () => {
            try {
                const orgResponse = await fetch(`${API_BASE_URL}/approvals/organization`);
                const orgData = await orgResponse.json();
                setDepartments(orgData.departments || []);
                setEmployees(orgData.employees || []);
                if (orgData.departments && orgData.departments.length > 0) {
                    // sabun으로 사용자의 부서를 찾아 기본 선택
                    const myEmployee = (orgData.employees || []).find(e => e.sabun === sabun);
                    const myDeptId = myEmployee ? myEmployee.deptId
                        : (initialDeptId || orgData.departments[0].id);
                    setSelectedDept(myDeptId);
                }
            } catch (err) {
                console.error("Fetch Org Error:", err);
                setDepartments([]);
                setEmployees([]);
            } finally { setLoading(false); }
        };
        fetchOrg();
    }, [initialDeptId, sabun]);

    // Auto-load previous approval path on mount (by tableCd)
    useEffect(() => {
        if (!tableCd) return;

        // 기안자(본인) 엔트리 생성 — 항상 Main Line 첫 행에 잠금 상태로 배치됨
        const makeDrafter = (lineId) => ({
            sabun, name: userName, deptId: '', pos_nm: 'Drafter', jikb_nm: '',
            lineId,
            instanceId: `${sabun}-${Date.now()}-default`
        });

        const loadPreviousPath = async () => {
            try {
                // 본인(sabun) 이 과거 상신한 결재선을 우선 로드
                const res = await fetch(
                    `${API_BASE_URL}/approvals/recent-path`
                    + `?tableCd=${encodeURIComponent(tableCd)}`
                    + `&sabun=${encodeURIComponent(sabun || '')}`
                );
                const data = await res.json();
                if (!data.found || !data.approvers?.length) {
                    // No previous path — add self as default approver
                    if (sabun && userName) {
                        setApprovers([makeDrafter(lines[0]?.id || 1)]);
                    }
                    return;
                }

                const lineMap = new Map();
                data.approvers.forEach(app => {
                    const lineId = Math.floor(parseInt(app.DOCU_SER) / 10);
                    if (!lineMap.has(lineId)) lineMap.set(lineId, []);
                    lineMap.get(lineId).push(app);
                });

                const newLines = [];
                const newApprovers = [];
                let lineIdx = 0;
                for (const [lineId, members] of [...lineMap.entries()].sort((a, b) => a[0] - b[0])) {
                    const style = COLOR_PALETTE[lineIdx % COLOR_PALETTE.length];
                    const isMain = lineId === 1;
                    newLines.push({
                        id: lineId,
                        title: isMain ? 'Main Line' : `Control Line ${lineId - 1}`,
                        type: isMain ? 'main' : 'control',
                        startSer: (lineId * 10) + (isMain ? 2 : 1),
                        ...style
                    });
                    members.forEach(m => {
                        newApprovers.push({
                            sabun: m.sabun, name: m.name,
                            deptId: m.deptId || '', pos_nm: m.pos_nm || '', jikb_nm: m.jikb_nm || '',
                            lineId: lineId,
                            instanceId: `${m.sabun}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`
                        });
                    });
                    lineIdx++;
                }

                // 기안자(본인) 를 Main Line 첫 행에 잠금 상태로 prepend
                // 백엔드(getRecentApprovalPath) 는 이미 SER=11(기안자)을 제외하고 실 결재자만 반환하므로
                // 별도 dedup 없이 그대로 prepend — 본인을 결재자 #2 로 지정한 SER=12 등도 보존됨
                const mainLine = newLines.find(l => l.type === 'main');
                if (sabun && userName && mainLine) {
                    newApprovers.unshift(makeDrafter(mainLine.id));
                }
                setLines(newLines);
                setApprovers(newApprovers);
            } catch (err) { console.error("Load previous path error:", err); }
        };
        loadPreviousPath();
    }, [tableCd]);

    const addLine = () => {
        const nextId = lines.length > 0 ? Math.max(...lines.map(l => l.id)) + 1 : 1;
        const nextSer = (nextId * 10) + 1; 
        const style = COLOR_PALETTE[(nextId - 1) % COLOR_PALETTE.length];

        setLines([...lines, { 
            id: nextId, 
            title: nextId === 1 ? 'Main Line' : `Control Line ${nextId - 1}`, 
            type: nextId === 1 ? 'main' : 'control', 
            startSer: nextSer, 
            ...style
        }]);
    };

    const removeLine = (lineId) => {
        if (lines.find(l => l.id === lineId)?.type === 'main') return;
        setLines(lines.filter(l => l.id !== lineId));
        setApprovers(approvers.filter(a => a.lineId !== lineId));
    };

    const filteredEmployees = selectedDept ? employees.filter(e => e.deptId === selectedDept) : [];
    
    const addApprover = (employee, lineId) => {
        const instanceId = `${employee.sabun}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        setApprovers([...approvers, { ...employee, lineId, instanceId }]);
    };

    const removeApprover = (instanceId) => {
        // 디폴트 본인(기안자)은 삭제 불가
        if (instanceId.endsWith('-default')) return;
        setApprovers(approvers.filter(a => a.instanceId !== instanceId));
    };
    
    const handleSubmit = () => {
        // 기안자(-default)는 SER=11 로 자동 추가되므로, 비-기안자 결재자가 최소 1명 필요
        const mainApprovers = approvers.filter(a =>
            lines.find(l => l.id === a.lineId)?.type === 'main'
            && !a.instanceId.endsWith('-default')
        );
        if (mainApprovers.length === 0) {
            alert('At least one Main Approver (other than yourself) is required.');
            return;
        }
        onSubmit();
    };

    if (loading) return <div className="h-screen flex items-center justify-center bg-gray-50 text-gray-500 font-bold">Loading Organization Data...</div>;

    return (
        <div className="h-screen flex flex-col bg-slate-50 overflow-hidden">
            {/* Header */}
            <div className="px-8 py-5 flex justify-between items-center bg-white border-b border-slate-200/80 shadow-sm">
                <div className="flex-1 flex items-center justify-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-200">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">
                            {mode === 'LINE_SELECT' ? 'Approval Line Setup' : 'Approval Request'}
                        </h2>
                        <p className="text-[11px] font-medium text-slate-400 mt-0.5">{docuNm}</p>
                    </div>
                </div>
                {showMultiLine && (
                    <button onClick={addLine} className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-5 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2 transition-all border border-slate-200">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
                        Add Line
                    </button>
                )}
            </div>

            {/* Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Departments */}
                <div className="flex-1 min-w-0 border-r border-slate-200/80 bg-white overflow-y-auto p-5">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.15em] mb-3 px-1">Department</p>
                    <div className="space-y-1">
                        {departments?.map(dept => (
                            <div key={dept.id} onClick={() => setSelectedDept(dept.id)}
                                className={`cursor-pointer px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                                    selectedDept === dept.id
                                        ? 'bg-gradient-to-r from-indigo-500 to-blue-600 text-white shadow-lg shadow-indigo-200/50'
                                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                            >{dept.name}</div>
                        ))}
                    </div>
                </div>

                {/* Employees */}
                <div className="flex-1 min-w-0 border-r border-slate-200/80 overflow-y-auto p-5 bg-slate-50/50">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.15em] mb-3 px-1">Members</p>
                    <div className="space-y-2">
                        {filteredEmployees?.map(emp => (
                            <div
                                key={emp.sabun}
                                draggable
                                onDragStart={(e) => e.dataTransfer.setData('application/json', JSON.stringify(emp))}
                                className="group px-4 py-3 border border-slate-200/80 rounded-xl bg-white flex items-center gap-3 cursor-grab active:cursor-grabbing hover:border-indigo-300 hover:shadow-md hover:shadow-indigo-100/50 transition-all duration-200"
                            >
                                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-sm font-bold text-slate-500 group-hover:from-indigo-100 group-hover:to-blue-100 group-hover:text-indigo-600 transition-all shrink-0">
                                    {emp.name?.charAt(0)}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-sm text-slate-800">{emp.name}</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">{emp.pos_nm} {emp.jikb_nm ? `| ${emp.jikb_nm}` : ''}</p>
                                </div>
                                <svg className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Approval Path */}
                <div className="flex-1 min-w-0 bg-white flex flex-col p-5 overflow-hidden">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.15em] mb-3 px-1">Approval Path</p>
                    <div className="flex-1 flex gap-3 overflow-x-auto pb-3">
                        {lines.map(line => (
                            <div
                                key={line.id}
                                className={`min-w-[200px] flex-1 flex flex-col rounded-2xl p-3 transition-all duration-200 ${
                                    dragOverLineId === line.id ? 'ring-2 ring-indigo-300 ring-offset-2 bg-indigo-50/30' : 'bg-slate-50/80'
                                }`}
                                onDragOver={(e) => { e.preventDefault(); setDragOverLineId(line.id); }}
                                onDragLeave={() => setDragOverLineId(null)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setDragOverLineId(null);
                                    try {
                                        const emp = JSON.parse(e.dataTransfer.getData('application/json'));
                                        addApprover(emp, line.id);
                                    } catch {}
                                }}
                            >
                                <div className={`relative flex items-center justify-center mb-3 rounded-xl ${line.bgColor} ${line.textColor} py-2.5 border ${line.border}`}>
                                    <span className="text-[10px] font-bold uppercase tracking-wider">{line.title}</span>
                                    {line.type !== 'main' && (
                                        <button onClick={() => removeLine(line.id)} className="absolute right-2.5 w-5 h-5 rounded-full bg-white/60 hover:bg-red-100 text-slate-400 hover:text-red-500 flex items-center justify-center text-xs transition-all">&times;</button>
                                    )}
                                </div>
                                <div className="flex-1 overflow-y-auto px-0.5">
                                    {(() => {
                                        const lineApprovers = approvers.filter(a => a.lineId === line.id);
                                        if (lineApprovers.length === 0) return (
                                            <div className="border-2 border-dashed border-slate-200 rounded-xl py-10 text-center text-slate-400 text-[11px]">Drag & drop here</div>
                                        );
                                        return (
                                            <div>
                                                {lineApprovers.map((app, idx, arr) => (
                                                    <div key={app.instanceId}>
                                                        <div className={`w-full px-4 py-3 bg-white rounded-xl shadow-sm border ${line.border} flex items-center gap-3`}>
                                                            <div className={`w-7 h-7 rounded-lg ${line.bgColor} flex items-center justify-center text-white text-[10px] font-bold shrink-0`}>
                                                                {idx + 1}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-sm font-semibold text-slate-800 truncate">{app.name}</p>
                                                                <p className="text-[10px] text-slate-400">{app.pos_nm}</p>
                                                            </div>
                                                            {!app.instanceId.endsWith('-default') && (
                                                                <button onClick={() => removeApprover(app.instanceId)} className="w-6 h-6 rounded-full hover:bg-red-50 text-slate-300 hover:text-red-500 flex items-center justify-center text-sm transition-all shrink-0">&times;</button>
                                                            )}
                                                        </div>
                                                        {idx < arr.length - 1 && (
                                                            <div className="flex justify-center py-1.5">
                                                                <svg className="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3"/></svg>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={handleSubmit}
                        disabled={approvers.filter(a => !a.instanceId.endsWith('-default')).length === 0 || isProcessing}
                        className="w-full mt-4 text-white font-semibold py-4 rounded-xl transition-all duration-200 disabled:opacity-30 text-sm shrink-0 flex items-center justify-center gap-2.5 bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 shadow-lg shadow-indigo-200/50 hover:shadow-xl hover:shadow-indigo-300/50 active:scale-[0.98]"
                    >
                        {isProcessing ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                            </>
                        ) : mode === 'LINE_SELECT' ? (
                            <>
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                                결재라인 저장
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
                                Submit Approval
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- 2. Step 2: Final Review Section ---
const FinalReviewSection = ({ params, detailUrl, approvers, lines, onBack, onConfirm, isProcessing }) => {
    return (
        <div className="h-screen flex flex-col bg-white">
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                <div>
                    <h2 className="text-2xl font-black text-gray-800 tracking-tight uppercase">STEP 02: FINAL REVIEW</h2>
                    <p className="text-xs text-gray-500 font-bold mt-1">Please confirm the approval line for this document.</p>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto p-10">
                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
                    <div className="space-y-8">
                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest border-b pb-2">Document Summary</h4>
                        <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100 space-y-6">
                            <div><label className="text-[10px] font-black text-gray-400 uppercase block mb-1">Module</label><p className="text-lg font-bold text-gray-700">{params.tableNm || params.tableCd}</p></div>
                            <div><label className="text-[10px] font-black text-gray-400 uppercase block mb-1">Subject</label><p className="text-2xl font-black text-gray-900 leading-tight">{params.docuNm}</p></div>
                            <div className="pt-6 border-t flex flex-col gap-4">
                                <div><label className="text-[10px] font-black text-gray-400 uppercase block mb-1">Applicant</label><p className="font-bold text-gray-700">{params.userName}</p></div>
                                <div><label className="text-[10px] font-black text-gray-400 uppercase block mb-1">Emp ID</label><p className="font-bold text-gray-700">{params.sabun}</p></div>
                            </div>
                            {detailUrl && (
                                <div className="pt-6 border-t">
                                    <a
                                        href={detailUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full inline-flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 font-black text-xs py-3 rounded-xl transition-all uppercase tracking-wider border border-blue-200"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                        ERP 문서 상세보기
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="lg:col-span-2 space-y-8">
                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest border-b pb-2">Full Approval Path</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {lines.map(line => (
                                <div key={line.id} className="space-y-4">
                                    <span className={`block text-center text-[10px] font-black py-2 rounded-lg uppercase ${line.bgColor} ${line.textColor} border ${line.border}`}>{line.title}</span>
                                    {approvers.filter(a => a.lineId === line.id).map((app, idx) => {
                                        const lineMembers = approvers.filter(a => a.lineId === line.id);
                                        const memberIdx = lineMembers.findIndex(a => a.instanceId === app.instanceId);
                                        const displaySer = line.startSer + memberIdx;
                                        return (
                                            <div key={app.instanceId} className={`p-4 bg-white border ${line.border} rounded-2xl shadow-sm`}>
                                                <p className="text-sm font-bold text-gray-800">{app.name}</p>
                                                <p className="text-[10px] text-gray-400 mt-0.5">{app.pos_nm}</p>
                                                <p className={`text-[9px] ${line.textColor} font-black font-mono mt-1`}>STEP: {displaySer}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className="p-8 border-t bg-gray-50 flex gap-6">
                <button 
                    onClick={onConfirm} 
                    disabled={isProcessing}
                    className={`flex-[2] py-5 rounded-2xl font-black text-white shadow-2xl transition-all uppercase tracking-widest text-base flex items-center justify-center gap-3 ${isProcessing ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                    {isProcessing ? (
                        <>
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Processing...</span>
                        </>
                    ) : 'Confirm & Register Approval'}
                </button>
                <button onClick={onBack} disabled={isProcessing} className="flex-1 bg-white text-gray-600 font-bold py-5 rounded-2xl border border-gray-200 hover:bg-gray-100 transition-all uppercase tracking-wider disabled:opacity-50">Modify Path</button>
            </div>
        </div>
    );
};

// --- MAIN PAGE COMPONENT ---
const ExternalApprovalPage = () => {
    const [searchParams] = useSearchParams();
    const [approvers, setApprovers] = useState([]);
    const [lines, setLines] = useState([
        { id: 1, title: 'Main Line', type: 'main', startSer: 12, ...COLOR_PALETTE[0] }
    ]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const params = {
        tableCd: searchParams.get('tableCd'),
        tableNm: searchParams.get('tableNm'),
        docuCd: searchParams.get('docuCd'),
        docuNo: searchParams.get('docuNo') || '1',
        docuNm: searchParams.get('docuNm'),
        sabun: searchParams.get('sabun'),
        jikb: searchParams.get('jikb'),
        userName: searchParams.get('userName'),
        pgmId: searchParams.get('pgmId'),
        detailUrl: searchParams.get('detailUrl'),
        screenshotUrl: searchParams.get('screenshotUrl'),
        multiLine: searchParams.get('multiLine'),
        // mode: ''(결재 실행, 기본) / 'LINE_SELECT'(결재라인 저장만 — postMessage 로 반환, 백엔드 호출 안 함)
        //       / 'NOTIFY_ONLY' (결재라인 INSERT 는 WinForms 가 완료, hub 는 메일만 발송)
        mode: (searchParams.get('mode') || '').toUpperCase()
    };

    // ─────────────────────────────────────────────────────────────
    // NOTIFY_ONLY 모드 — WinForms 가 이미 COM_CLS_MST INSERT 완료한 케이스.
    // 사용자에게 "정말 결재상신 하시겠습니까?" 확인 후 Yes 시 메일 발송.
    // 결재라인 선택 UI 는 보이지 않음 (별도 렌더 분기에서 처리).
    // ─────────────────────────────────────────────────────────────
    const handleNotifyOnlyConfirm = async () => {
        if (!params.tableCd || !params.docuCd || !params.sabun) {
            alert('필수 파라미터 누락');
            return;
        }
        setIsProcessing(true);
        try {
            const finalScreenshotUrl = params.screenshotUrl || window.__screenshotUrl || '';
            const resp = await fetch(`${API_BASE_URL}/approvals/external/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...params, screenshotUrl: finalScreenshotUrl, mode: 'NOTIFY_ONLY' })
            });
            if (!resp.ok) {
                const data = await resp.json().catch(() => ({}));
                throw new Error(data.message || 'Notification failed');
            }
            setIsSubmitted(true);
            setTimeout(() => {
                if (window.chrome && window.chrome.webview) {
                    try { window.chrome.webview.postMessage({ type: 'APPROVAL_SUCCESS' }); } catch (e) {}
                }
                window.close();
            }, 800);
        } catch (err) {
            console.error('[NOTIFY_ONLY] failed:', err);
            alert('메일 발송 실패: ' + err.message);
            setIsProcessing(false);
        }
    };

    const handleNotifyOnlyCancel = () => {
        // 메일 발송 안 함 — DB 는 이미 저장된 상태 (WinForms 에서 INSERT 완료)
        if (window.chrome && window.chrome.webview) {
            try { window.chrome.webview.postMessage({ type: 'APPROVAL_CANCEL' }); } catch (e) {}
        }
        window.close();
    };

    const getFormattedApprovers = () => {
        // 기안자(-default) 는 백엔드가 SER=11 로 자동 추가하므로 approvers 배열에서 제외
        // DOCU_SER 계산 시 같은 라인 내 비-기안자 인덱스만 사용
        const realApprovers = approvers.filter(a => !a.instanceId.endsWith('-default'));
        return realApprovers.map(app => {
            const line = lines.find(l => l.id === app.lineId);
            const lineMembers = realApprovers.filter(a => a.lineId === app.lineId);
            const memberIdx = lineMembers.findIndex(a => a.instanceId === app.instanceId);
            return {
                sabun: app.sabun,
                name: app.name,
                ser: (line.startSer + memberIdx).toString(),
                type: line.type
            };
        });
    };

    // LINE_SELECT 모드 전용 — ERP 결재 타임라인이 기대하는 풍부한 포맷
    // [{ sabun, kname, jikb, role, stepOrder, lineType }, ...]
    const getApproversForLineSelect = () => {
        const realApprovers = approvers.filter(a => !a.instanceId.endsWith('-default'));
        // Main Line → Control Lines 순으로 정렬 + 각 라인 안에서 순서 유지
        const sorted = [...realApprovers].sort((a, b) => {
            const la = lines.find(l => l.id === a.lineId);
            const lb = lines.find(l => l.id === b.lineId);
            if (!la || !lb) return 0;
            if (la.type !== lb.type) return la.type === 'main' ? -1 : 1;
            return la.id - lb.id;
        });
        return sorted.map((app, idx) => {
            const line = lines.find(l => l.id === app.lineId);
            return {
                sabun:     app.sabun || '',
                kname:     app.name  || '',
                name:      app.name  || '',                  // 별칭 (호환)
                jikb:      app.jikb  || '',                  // 직급 코드 (있으면)
                jikb_nm:   app.jikb_nm || '',                // 직급명 (한글)
                pos_nm:    app.pos_nm  || '',                // 직책명 (한글, 예: "팀장")
                role:      app.jikb_nm || app.pos_nm || '',  // 역할 표시명 (호환)
                stepOrder: idx + 1,
                lineType:  line ? line.type : 'main',
                lineId:    app.lineId
            };
        });
    };

    const closeWindow = () => {
        window.close();
        if (!window.closed) window.open('about:blank', '_self').close();
        if (window.chrome && window.chrome.webview) window.chrome.webview.postMessage({ type: 'APPROVAL_SUCCESS' });
    };

    // LINE_SELECT 모드 — 백엔드 호출 없이 결재자 정보만 postMessage 로 ERP 에 반환.
    // ERP(COM_S002_APPROVAL_WEB_PROC)가 메시지 수신 후 다이얼로그를 직접 닫으므로
    // hub 측은 별도 완료 화면 표시 없이 메시지만 송신.
    const handleLineSelectSave = () => {
        setIsProcessing(true);
        try {
            const approversForErp = getApproversForLineSelect();
            if (!approversForErp || approversForErp.length === 0) {
                alert('Please select approvers.');
                setIsProcessing(false);
                return;
            }
            const payload = { type: 'LINE_SELECT_SAVED', approvers: approversForErp };
            console.log('[hub] LINE_SELECT_SAVED payload →', payload);
            // WebView2 호스트로 전달 — ERP COM_S002_APPROVAL_WEB_PROC 가 수신 후 다이얼로그 close
            if (window.chrome && window.chrome.webview) {
                try {
                    window.chrome.webview.postMessage(payload);
                    console.log('[hub] postMessage(object) OK');
                } catch (e) {
                    // 객체 직렬화 실패 환경 — 문자열 폴백
                    console.warn('[hub] postMessage(object) failed, falling back to string', e);
                    try {
                        window.chrome.webview.postMessage('LINE_SELECT_SAVED:' + JSON.stringify(approversForErp));
                        console.log('[hub] postMessage(string fallback) OK');
                    } catch (e2) {
                        console.error('[hub] postMessage(string fallback) failed', e2);
                    }
                }
                // ERP 가 닫을 때까지 isProcessing 유지 (사용자가 다시 클릭 못 하게)
            } else {
                // 디버깅용 — 브라우저에서 직접 열었을 때만 confirmation
                console.log('[LINE_SELECT_SAVED] (no WebView2 host)', payload);
                alert('결재라인이 저장되었습니다. (WebView2 호스트 없음 — 미리보기 모드)');
                setIsProcessing(false);
            }
        } catch (err) {
            alert(err.message);
            setIsProcessing(false);
        }
    };

    const handleFinalSubmit = async () => {
        // LINE_SELECT 모드 — 결재라인 저장만 (백엔드 호출 안 함)
        if (params.mode === 'LINE_SELECT') {
            handleLineSelectSave();
            return;
        }

        setIsProcessing(true);
        try {
            const formatted = getFormattedApprovers();
            console.log('--- Sending Approval Data ---', formatted);

            if (!formatted || formatted.length === 0) {
                alert('Please select approvers.');
                setIsProcessing(false);
                return;
            }

            // 백그라운드 업로드 완료 시 window.__screenshotUrl에 URL이 주입됨 (WinForms → WebView2)
            const finalScreenshotUrl = params.screenshotUrl || window.__screenshotUrl || '';

            const response = await fetch(`${API_BASE_URL}/approvals/external/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...params, screenshotUrl: finalScreenshotUrl, approvers: formatted }),
            });
            if (!response.ok) {
                // 서버 거부 사유(이미 결재 중·완료 409, 키·결재자 오류 400)를 그대로 보여준다
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || 'Submission failed');
            }
            setIsSubmitted(true);
            setTimeout(closeWindow, 2000);
        } catch (err) {
            alert(err.message);
            setIsProcessing(false);
        }
    };

    if (!params.tableCd || !params.docuCd || !params.sabun) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-100 p-10">
                <div className="bg-white p-10 rounded-3xl shadow-xl max-w-md text-center border-t-8 border-red-500">
                    <div className="text-5xl mb-6">🚫</div>
                    <h3 className="text-xl font-black text-gray-800 uppercase mb-2">Invalid Request</h3>
                    <p className="text-gray-500 leading-relaxed text-sm">Required parameters (tableCd, docuCd, sabun) are missing.</p>
                </div>
            </div>
        );
    }

    // NOTIFY_ONLY 모드 — 결재라인 선택 UI 숨김. 큰 확인 다이얼로그 표시 후 Yes/No.
    if (params.mode === 'NOTIFY_ONLY') {
        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50 p-8">
                <div className="bg-white rounded-3xl shadow-2xl shadow-indigo-200/40 max-w-lg w-full p-10 text-center">
                    {isSubmitted ? (
                        <>
                            <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-emerald-50 flex items-center justify-center animate-[bounce_1s_ease-in-out_2]">
                                <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                            </div>
                            <h3 className="text-2xl font-extrabold text-slate-900 mb-2">Approval Submitted</h3>
                            <p className="text-sm text-slate-500">Notification email sent to the approver.</p>
                        </>
                    ) : isProcessing ? (
                        <>
                            <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-indigo-50 flex items-center justify-center">
                                <svg className="w-7 h-7 text-indigo-600 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                            </div>
                            <h3 className="text-2xl font-extrabold text-slate-900 mb-2">Sending Email…</h3>
                            <p className="text-sm text-slate-500">Notifying the approver.</p>
                        </>
                    ) : (
                        <>
                            <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-indigo-50 flex items-center justify-center">
                                <svg className="w-7 h-7 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9.5"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01"/></svg>
                            </div>
                            <h3 className="text-2xl font-extrabold text-slate-900 mb-2">Confirm Approval</h3>
                            <p className="text-sm text-slate-500 mb-7">Are you sure you want to approve?</p>
                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={handleNotifyOnlyCancel}
                                    className="px-12 py-2.5 rounded-lg text-sm font-bold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 transition-all active:scale-95">
                                    No
                                </button>
                                <button
                                    onClick={handleNotifyOnlyConfirm}
                                    className="px-12 py-2.5 rounded-lg text-sm font-bold text-white bg-indigo-700 hover:bg-indigo-800 shadow-md shadow-indigo-300/40 transition-all active:scale-95">
                                    Yes
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    if (isSubmitted) {
        const isLineSelect = params.mode === 'LINE_SELECT';
        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
                <div className="text-center max-w-sm">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-white border border-slate-200 flex items-center justify-center shadow-lg shadow-slate-200/50 animate-[bounce_1s_ease-in-out_2]">
                        <svg className="w-10 h-10 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-2">
                        {isLineSelect ? '결재라인 저장 완료' : 'Approval Submitted'}
                    </h3>
                    <p className="text-sm text-slate-400 mb-8">
                        {isLineSelect
                            ? '선택한 결재라인이 ERP 신청서에 반영되었습니다.'
                            : 'Your approval request has been sent successfully.'}
                    </p>
                    <button onClick={closeWindow} className="px-8 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 shadow-lg shadow-indigo-200/50 transition-all active:scale-95">
                        Close
                    </button>
                </div>
            </div>
        );
    }

    return (
        <ApprovalLineSection
            initialDeptId=""
            docuNm={params.docuNm}
            tableCd={params.tableCd}
            sabun={params.sabun}
            userName={params.userName}
            showMultiLine={params.multiLine === 'Y'}
            approvers={approvers}
            setApprovers={setApprovers}
            lines={lines}
            setLines={setLines}
            onSubmit={handleFinalSubmit}
            isProcessing={isProcessing}
            mode={params.mode}
        />
    );
};

export default ExternalApprovalPage;
